import { AppConfig } from '@/types';
import { STORAGE_KEYS, DEFAULT_CONFIG, VOICE_IDS } from '@/lib/constants';

/**
 * Generate Base64 encoded authId from username and password
 */
export function generateAuthId(username: string, password: string): string {
  return btoa(`${username}:${password}`);
}

/**
 * Map voice gender to voice ID
 */
export function mapVoiceId(gender: 'male' | 'female' | 'female2' | 'female3'): string {
  return VOICE_IDS[gender];
}

/**
 * Validate app configuration
 */
export function validateConfig(config: Partial<AppConfig>): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Required fields
  if (!config.username?.trim()) {
    errors.push('Username is required');
  }
  if (!config.password?.trim()) {
    errors.push('Password is required');
  }
  if (!config.websocketUrl?.trim()) {
    errors.push('WebSocket URL is required');
  }
  if (!config.websocketPath?.trim()) {
    errors.push('WebSocket path is required');
  }
  if (!config.voiceAPIKey?.trim()) {
    errors.push('Voice API key is required');
  }

  // Validate URL format
  if (config.websocketUrl) {
    try {
      new URL(config.websocketUrl);
    } catch {
      errors.push('WebSocket URL must be a valid HTTPS URL');
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Save configuration to localStorage
 */
export function saveConfig(config: AppConfig): void {
  try {
    // Generate authId if username/password changed
    const authId = generateAuthId(config.username, config.password);

    // Map voice gender to voice ID
    const voiceId = mapVoiceId(config.voiceGender);

    const configToSave: AppConfig = {
      ...config,
      authId,
      voiceId,
      version: DEFAULT_CONFIG.version,
      lastConnected: Date.now(),
    };

    localStorage.setItem(
      STORAGE_KEYS.APP_CONFIG,
      JSON.stringify(configToSave)
    );
  } catch (error) {
    console.error('Failed to save config:', error);
    throw new Error('Failed to save configuration');
  }
}

/**
 * Load configuration from localStorage
 */
export function loadConfig(): AppConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.APP_CONFIG);
    if (!stored) {
      return null;
    }

    const config = JSON.parse(stored) as AppConfig;

    // Validate version compatibility
    if (config.version !== DEFAULT_CONFIG.version) {
      console.warn(
        `Config version mismatch: ${config.version} vs ${DEFAULT_CONFIG.version}`
      );
      // Could implement migration logic here
    }

    return config;
  } catch (error) {
    console.error('Failed to load config:', error);
    return null;
  }
}

/**
 * Clear configuration from localStorage
 */
export function clearConfig(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.APP_CONFIG);
  } catch (error) {
    console.error('Failed to clear config:', error);
  }
}

/**
 * Get default configuration
 */
export function getDefaultConfig(): AppConfig {
  return { ...DEFAULT_CONFIG };
}

/**
 * Update specific config fields
 */
export function updateConfig(updates: Partial<AppConfig>): void {
  const current = loadConfig() || getDefaultConfig();
  const updated = { ...current, ...updates };
  saveConfig(updated);
}
